import { useEffect, useRef, useState } from 'react';
import { CloseIcon } from './Icons.js';

// Top-level legal section headings ("1.\tÜMUMİ ŞƏRTLƏR") are the lines the
// backend wraps in <b> for the Android app; sub-clauses ("1.1.") are not.
const SECTION_HEADING = /^\d+\.\s+\S/;
const CLAUSE = /^(\d+(?:\.\d+)*\.)\s+([\s\S]*)$/;

const LegalText = ({ text }) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      if (SECTION_HEADING.test(line) && !/^\d+\.\d/.test(line)) {
        return (
          <h3 key={index} className="legal__heading">
            {line.replace(/\s+/, ' ')}
          </h3>
        );
      }
      const clause = line.match(CLAUSE);
      if (clause) {
        return (
          <p key={index} className="legal__clause">
            <span className="legal__num">{clause[1]}</span>
            <span>{clause[2]}</span>
          </p>
        );
      }
      return (
        <p key={index} className="legal__para">
          {line}
        </p>
      );
    });

const FOCUSABLE = 'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])';

const Modal = ({ isOpen, onClose, title, text, closeLabel }) => {
  const [isMounted, setIsMounted] = useState(isOpen);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const bodyRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) setIsMounted(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isMounted) return undefined;

    returnFocusRef.current = document.activeElement;
    closeRef.current?.focus({ preventScroll: true });
    if (bodyRef.current) bodyRef.current.scrollTop = 0;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(FOCUSABLE);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus?.({ preventScroll: true });
    };
  }, [isOpen, isMounted, onClose]);

  if (!isMounted) return null;

  const handleAnimationEnd = (event) => {
    if (!isOpen && event.target === event.currentTarget) setIsMounted(false);
  };

  return (
    <div
      className="modal"
      data-state={isOpen ? 'open' : 'closed'}
      onClick={onClose}
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        ref={dialogRef}
        className="modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <h2 id="modal-title" className="modal__title">
            {title}
          </h2>
          <button ref={closeRef} type="button" className="icon-button" onClick={onClose} aria-label={closeLabel}>
            <CloseIcon size={22} />
          </button>
        </header>
        <div ref={bodyRef} className="modal__body legal" tabIndex={0}>
          <LegalText text={text || ''} />
        </div>
      </div>
    </div>
  );
};

export default Modal;
